"use client";

import { CustomButton, FlexCenter, FlexCol } from "@/components";
import InputDataContent from "@/components/inquiryDetails/inputDataContent";
import { IBaseLabelValue } from "@/interfaces";
import { IRawPurchasedThirdPartyInsuranceInquiryResData } from "@/interfaces/inquiries/vehicle";
import { formatNumberWithDots } from "@/lib";
import { routes } from "@/routes";
import { useRouter } from "next/navigation";

const valueOrDash = (value?: string | number | null) =>
  value === undefined || value === null || value === "" ? "-" : String(value);

const formatNumber = (value?: string | number | null) => {
  if (value === undefined || value === null || value === "") return "-";
  return formatNumberWithDots(value) ?? String(value);
};

const formatRawNumber = (value?: string | number | null) => valueOrDash(value);

const formatRial = (value?: string | number | null) => {
  const formattedValue = formatNumber(value);
  return formattedValue === "-" ? "-" : `${formattedValue} ریال`;
};

const formatPercent = (value?: string | number | null) => {
  if (value === undefined || value === null || value === "") return "-";
  return `${formatNumber(value)}٪`;
};

const buildPlateText = (input?: Record<string, string | undefined>) => {
  const plateParts = [
    input?.plk_serial,
    input?.plk2,
    input?.plk3,
    input?.plk1,
  ].filter(Boolean);

  return plateParts.length ? plateParts.join(" ") : "-";
};

const damageTypesText = (items?: unknown[]) => {
  if (!Array.isArray(items) || items.length === 0) return "موردی ثبت نشده";

  return items
    .map((item) => {
      if (typeof item === "string" || typeof item === "number") return item;
      if (item && typeof item === "object") {
        const record = item as Record<string, unknown>;
        return (
          record.title ??
          record.name ??
          record.description ??
          record.damageType ??
          JSON.stringify(record)
        );
      }

      return null;
    })
    .filter(Boolean)
    .join("، ");
};

type ResultSection = {
  title: string;
  items: IBaseLabelValue[];
};

const createItem = (
  label: string,
  value: string | number | null | undefined,
  formatter: (value?: string | number | null) => string = valueOrDash,
): IBaseLabelValue => ({
  label,
  value: formatter(value),
});

const ResultSectionCard = ({ section }: { section: ResultSection }) => (
  <section className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4 sm:p-5">
    <h3 className="mb-4 text-sm font-bold text-primary-main sm:text-base">
      {section.title}
    </h3>
    <div className="grid gap-3 sm:grid-cols-2">
      {section.items.map((item) => (
        <div
          key={item.label}
          className="rounded-xl border border-white bg-white px-3 py-3 shadow-sm"
        >
          <span className="mb-1 block text-xs font-medium text-slate-500">
            {item.label}
          </span>
          <span className="break-words text-sm font-semibold leading-7 text-slate-800">
            {item.value}
          </span>
        </div>
      ))}
    </div>
  </section>
);

const ThirdPartyInsuranceInquiryDetails = ({
  data,
}: {
  data: IRawPurchasedThirdPartyInsuranceInquiryResData;
}) => {
  const router = useRouter();
  const storedPayload = (data as any)?.data ?? {};
  const inquiryData = storedPayload?.data ?? storedPayload;
  const result = inquiryData?.result ?? inquiryData;
  const inquiryInput =
    storedPayload?.input ?? (data as any).user_input ?? inquiryData?.input ?? {};

  const inputContent: IBaseLabelValue[] = [
    {
      label: "کد ملی مالک",
      value: valueOrDash(inquiryInput?.national_code || inquiryInput?.nationalCode),
    },
    {
      label: "شماره پلاک",
      value: buildPlateText(inquiryInput),
    },
  ];

  const resultSections: ResultSection[] = [
    {
      title: "وضعیت و اعتبار بیمه",
      items: [
        createItem("شرکت بیمه‌گر", result?.companyName),
        createItem("کد شرکت بیمه", result?.companyCode, formatRawNumber),
        createItem("تاریخ صدور بیمه‌نامه", result?.issueDate),
        createItem("تاریخ شروع اعتبار", result?.startDate),
        createItem("تاریخ پایان اعتبار", result?.endDate),
        createItem("کد یکتای بیمه‌نامه", result?.thirdPolicyCode, formatRawNumber),
        createItem("شماره بیمه‌نامه / سند شرکت", result?.prntPlcyCmpDocNo),
        createItem(
          "شماره سند/بیمه‌نامه قبلی",
          result?.lastCompanyDocumentNumber,
        ),
        createItem(
          "وضعیت الحاقیه",
          result?.printEndorsCompanyDocumentNumber,
        ),
        createItem("تاریخ الحاقیه / ثبت مرتبط", result?.endorseDate),
        createItem("توضیحات الحاقیه", result?.endorseText, (value) =>
          valueOrDash(value) === "-" ? "خالی" : valueOrDash(value),
        ),
        createItem("کد رهگیری", inquiryData?.trackId),
      ],
    },
    {
      title: "پوشش‌های مالی و جانی",
      items: [
        createItem("سقف تعهد خسارت بدنی", result?.lifeCvrCptl, formatRial),
        createItem(
          "سقف تعهد خسارت مالی",
          result?.financialCvrCptl,
          formatRial,
        ),
      ],
    },
    {
      title: "تخفیف‌ها",
      items: [
        createItem("درصد تخفیف حوادث راننده", result?.disPrsnYrPrcnt, formatPercent),
        createItem("تعداد سال تخفیف حوادث راننده", result?.disPrsnYrNum, formatNumber),
        createItem("درصد تخفیف خسارت مالی", result?.disFnYrPrcnt, formatPercent),
        createItem("تعداد سال تخفیف خسارت مالی", result?.disFnYrNum, formatNumber),
        createItem("درصد تخفیف خسارت جانی", result?.disLfYrPrcnt, formatPercent),
        createItem("تعداد سال تخفیف خسارت جانی", result?.disLfYrNum, formatNumber),
        createItem("درصد تخفیف شخصی", result?.discountPersonPercent, (value) =>
          valueOrDash(value) === "-" ? "خالی" : formatPercent(value),
        ),
        createItem("درصد تخفیف شخص ثالث", result?.discountThirdPercent, (value) =>
          valueOrDash(value) === "-" ? "خالی" : formatPercent(value),
        ),
      ],
    },
    {
      title: "سوابق خسارت",
      items: [
        createItem("تعداد خسارت‌های ثبت‌شده", result?.countLossAmount, formatNumber),
        createItem("تعداد خسارت‌های جانی", result?.policyHealthLossCount, formatNumber),
        createItem("تعداد خسارت‌های مالی", result?.policyFinancialLossCount, formatNumber),
        createItem(
          "تعداد خسارت‌های مربوط به راننده/شخص",
          result?.policyPersonLossCount,
          formatNumber,
        ),
        {
          label: "انواع خسارت ثبت‌شده",
          value: damageTypesText(result?.damageTypes),
        },
      ],
    },
    {
      title: "مشخصات خودرو",
      items: [
        createItem("شماره VIN", result?.vin),
        createItem("شماره موتور", result?.mtrNum),
        createItem("شماره شاسی", result?.shsNum),
        createItem("سیستم / برند خودرو", result?.mapVehicleSystemName),
        createItem("سال ساخت", result?.modelCii, formatRawNumber),
        createItem("تناژ", result?.tonage, formatNumber),
        createItem("کد گروه خودرو", result?.carGroupCode, formatRawNumber),
        createItem("تعداد سیلندر", result?.cylCnt, formatNumber),
        createItem("نوع کاربری", result?.mapUsageName, (value) =>
          valueOrDash(value) === "-" ? "ثبت نشده" : valueOrDash(value),
        ),
        createItem("کد کاربری", result?.mapUsageCode, formatRawNumber),
        createItem("نوع خودرو", result?.mapTypNam, (value) =>
          valueOrDash(value) === "-" ? "ثبت نشده" : valueOrDash(value),
        ),
        createItem("کد سیستم خودرو", result?.vehicleSystemCode, formatRawNumber),
        createItem("نام سیستم CII", result?.systemNameCii, (value) =>
          valueOrDash(value) === "-" ? "ثبت نشده" : valueOrDash(value),
        ),
        createItem("کد سیستم CII", result?.systemCodeCii, formatRawNumber),
        createItem("نام تیپ CII", result?.typeNameCii, (value) =>
          valueOrDash(value) === "-" ? "ثبت نشده" : valueOrDash(value),
        ),
        createItem("کد تیپ CII", result?.typeCodeCii, formatRawNumber),
        createItem("نام کاربری CII", result?.usageNameCii, (value) =>
          valueOrDash(value) === "-" ? "ثبت نشده" : valueOrDash(value),
        ),
        createItem("کد کاربری CII", result?.usageCodeCii, formatRawNumber),
        createItem("دو رقم ابتدایی پلاک", result?.plk1, formatRawNumber),
        createItem("کد حرف پلاک", result?.plk2, formatRawNumber),
        createItem("سه رقم پلاک", result?.plk3, formatRawNumber),
        createItem("سری / کد ایران", result?.plkSrl, formatRawNumber),
      ],
    },
  ];

  return (
    <div className="shadow-main from-sky-bliss relative basis-[500px] rounded-2xl bg-linear-to-b to-transparent p-[2px] lg:basis-[640px]">
      <FlexCol className="items-stretch rounded-2xl bg-white p-6 sm:p-8">
        <h2 className="mb-8 text-center font-bold lg:text-lg">
          نتیجه استعلام سوابق بیمه‌نامه شخص ثالث
        </h2>

        <InputDataContent
          content={inputContent}
          className="mb-6 p-6 md:p-8"
          itemClassName="flex-col md:flex-row leading-9 md:leading-none"
        />

        <div className="mb-8 flex flex-col gap-4">
          {resultSections.map((section) => (
            <ResultSectionCard key={section.title} section={section} />
          ))}
        </div>

        <FlexCenter className="flex flex-col justify-center md:flex-row md:gap-6">
          <CustomButton
            id="details-third-party-insurance-back"
            className="w-fit bg-gray-500"
            onClick={() => router.push(routes.main.INQUIRY_HISTORY.primaryPath)}
          >
            بازگشت
          </CustomButton>
          <CustomButton
            id="details-third-party-insurance-new-request"
            onClick={() =>
              router.push(routes.services.Third_Party_Insurance.rewritePath)
            }
            className="bg-primary-main mx-auto w-fit px-8! text-white"
          >
            استعلام جدید
          </CustomButton>
        </FlexCenter>
      </FlexCol>
    </div>
  );
};

export default ThirdPartyInsuranceInquiryDetails;
